import { lazy, Suspense, useMemo } from 'react';
import { Refine, Authenticated, useGetIdentity, useIsAuthenticated } from '@refinedev/core';
import routerProvider, { NavigateToResource, CatchAllNavigate } from '@refinedev/react-router';
import { Route, Routes, Outlet } from 'react-router';
import { App as AntdApp, Typography, ConfigProvider } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useNotificationProvider, ErrorComponent } from '@refinedev/antd';
import { AccessControlClient } from '@admin-panel/providers/lib/access/accesscontrolprovider';
import {
  getAdminVendorFacilityId,
  getAdminVendorId,
} from '@admin-panel/providers/lib/adminVendorHeader';
import { LKAuthProvider } from '@admin-panel/providers/lib/authProvider';
import { restDataProvider } from '@admin-panel/providers/lib/dataprovider';
import { LkNotificationsProvider } from '@admin-panel/providers/lib/notifications';
import { ThemeProvider } from '@admin-panel/config/theme';
import { AccessControlConfig, getResources } from '@admin-panel/access-control';
import LKLayout from '@admin-panel/components/lib/Layout';
import { GlobalProvider, VendorGlobalProvider, useVendorGlobalContext } from '@admin-panel/hooks';
import type { IERPUser } from '@admin-panel/types';
import { FoundationLoader } from '@admin-panel/ui';
import { lazyNamedExport } from '@admin-panel/utils';
import '@refinedev/antd/dist/reset.css';
import 'react-quill/dist/quill.snow.css';

import './global.css';

const ForegroundNotifications = lazy(() => import('./ForegroundNotifications'));
const Login = lazyNamedExport<{ title?: string }>(
  () => import('@admin-panel/pages/auth/login'),
  'Login',
);
const VendorForgotPassword = lazy(() => import('@admin-panel/pages/auth/VendorForgotPassword'));
const VendorResetPassword = lazy(() => import('@admin-panel/pages/auth/VendorResetPassword'));
const HomeDashboard = lazyNamedExport(
  () => import('@admin-panel/pages/dashboard'),
  'HomeDashboard',
);
const MTOHomepagePreview = lazyNamedExport(
  () => import('@admin-panel/pages/dashboard/MTOHomepagePreview'),
  'MTOHomepagePreview',
);
const NexusGRNDashboard = lazyNamedExport(
  () => import('@admin-panel/pages/dashboard/NexusGRNDashboard'),
  'NexusGRNDashboard',
);
const ShowOrderPackage = lazyNamedExport(
  () => import('@admin-panel/pages/orders/packages/show'),
  'ShowOrderPackage',
);
const VendorCustomerOrderPackages = lazyNamedExport(
  () => import('@admin-panel/pages/orders/packages/vendor'),
  'VendorCustomerOrderPackages',
);
const OutgoingManifests = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass/gatepass-manifest'),
  'OutgoingManifests',
);
const CreateManifest = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass/gatepass-manifest'),
  'CreateManifest',
);
const ShowManifest = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass/gatepass-manifest'),
  'ShowManifest',
);
const ManifestHelp = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass/gatepass-manifest'),
  'ManifestHelp',
);
const VendorPOItems = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/vendor/vendor_items_list'),
  'VendorPOItems',
);
const ShowOrderLine = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/show'),
  'ShowOrderLine',
);
const ListVendorSkus = lazyNamedExport(
  () => import('@admin-panel/pages/skus/vendor/vendor_skus_list'),
  'ListVendorSkus',
);
const ListVendorJITInventory = lazyNamedExport(
  () => import('@admin-panel/pages/vendorinventory'),
  'ListVendorJITInventory',
);
const ShowReturnRequest = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/return'),
  'ShowReturnRequest',
);
const AcceptMTOItemPO = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/vendor/accept_mto_item_po'),
  'AcceptMTOItemPO',
);
const AcceptMTOItemPOError = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/vendor/accept_mto_item_po'),
  'AcceptMTOItemPOError',
);
const AcceptMTOItemPOSuccess = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/vendor/accept_mto_item_po'),
  'AcceptMTOItemPOSuccess',
);
const RejectMTOItemPO = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/vendor/reject_mto_item_po'),
  'RejectMTOItemPO',
);
const RejectMTOItemPOError = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/vendor/reject_mto_item_po'),
  'RejectMTOItemPOError',
);
const RejectMTOItemPOSuccess = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/vendor/reject_mto_item_po'),
  'RejectMTOItemPOSuccess',
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
    },
    mutations: {
      retry: 0,
    },
  },
});

function Application() {
  const resources = useMemo(() => getResources('VENDOR'), []);
  const accessControlClient = useMemo(() => new AccessControlClient(), []);

  return (
    <ThemeProvider
      theme={{
        token: { colorPrimary: '#393AFB', colorBgLayout: '#ffffff' },
      }}
    >
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#393AFB',
            colorBgLayout: '#ffffff',
            fontFamily: '"Hanken Grotesk"',
            colorText: '#171717',
          },
        }}
      >
        <AntdApp>
          <QueryClientProvider client={queryClient}>
            <Refine
              authProvider={LKAuthProvider}
              accessControlProvider={accessControlClient.getAccessControlProvider()}
              dataProvider={restDataProvider}
              notificationProvider={useNotificationProvider}
              routerProvider={routerProvider}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                projectId: 'Unny6d-jT5kpU-QOXAra',
              }}
              resources={resources}
            >
              <AccessControlConfig resources={resources} dataprovider={accessControlClient}>
                <Routes>
                  <Route path="/mtoitem/accept/:code" element={<AcceptMTOItemPO />} />
                  <Route path="/mtoitem/reject/:code" element={<RejectMTOItemPO />} />
                  <Route path="/mtoitem/accept-success" element={<AcceptMTOItemPOSuccess />} />
                  <Route path="/mtoitem/accept-failure" element={<AcceptMTOItemPOError />} />
                  <Route path="/mtoitem/reject-success" element={<RejectMTOItemPOSuccess />} />
                  <Route path="/mtoitem/reject-failure" element={<RejectMTOItemPOError />} />
                  <Route
                    element={
                      <Authenticated
                        key="authenticated-routes"
                        fallback={<CatchAllNavigate to="/login" />}
                      >
                        <GlobalProvider>
                          <VendorGlobalProvider>
                            <LkNotificationsProvider>
                              <LKLayout>
                                <MainOutlet />
                              </LKLayout>
                            </LkNotificationsProvider>
                          </VendorGlobalProvider>
                        </GlobalProvider>
                      </Authenticated>
                    }
                  >
                    <Route index path="/" element={<HomeDashboard />} />
                    <Route index path="/grndashboard" element={<NexusGRNDashboard />} />
                    <Route path="/mto-homepage-preview" element={<MTOHomepagePreview />} />

                    <Route path="/lineitems" element={<VendorPOItems />} />
                    <Route path="/lineitems/show/:id" element={<ShowOrderLine />} />

                    <Route path="/packages">
                      <Route index path="" element={<VendorCustomerOrderPackages />} />
                      <Route path="show/:id" element={<ShowOrderPackage />} />
                    </Route>
                    <Route path="/manifests">
                      <Route index path="" element={<OutgoingManifests />} />
                      <Route path="help" element={<ManifestHelp />} />
                      <Route path="create" element={<CreateManifest />} />
                      <Route path=":id/show" element={<ShowManifest />} />
                    </Route>
                    <Route path="/skus">
                      <Route index path="" element={<ListVendorSkus />} />
                    </Route>
                    <Route path="/vendorinventory">
                      <Route index path="" element={<ListVendorJITInventory />} />
                    </Route>
                    <Route path="returns/show/:id" index element={<ShowReturnRequest />} />
                  </Route>
                  <Route
                    path="/forgot-password"
                    element={
                      <Suspense fallback={<FoundationLoader />}>
                        <VendorForgotPassword />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/reset-password"
                    element={
                      <Suspense fallback={<FoundationLoader />}>
                        <VendorResetPassword />
                      </Suspense>
                    }
                  />
                  <Route element={<AuthPageGate />}>
                    <Route
                      path="/login"
                      element={
                        <Suspense fallback={<FoundationLoader />}>
                          <Login title="Welcome to Nexus" />
                        </Suspense>
                      }
                    />
                  </Route>
                  <Route
                    element={
                      <Authenticated key="catch-all">
                        <GlobalProvider>
                          <VendorGlobalProvider>
                            <LkNotificationsProvider>
                              <LKLayout>
                                <Outlet />
                              </LKLayout>
                            </LkNotificationsProvider>
                          </VendorGlobalProvider>
                        </GlobalProvider>
                      </Authenticated>
                    }
                  >
                    <Route path="*" element={<ErrorComponent />} />
                  </Route>
                </Routes>
              </AccessControlConfig>
            </Refine>
          </QueryClientProvider>
        </AntdApp>
      </ConfigProvider>
    </ThemeProvider>
  );
}

const isNexusSuperAdmin = (user?: IERPUser) =>
  Boolean(user?.roles?.some(role => role.name === 'NEXUS_SUPER_ADMIN'));

const AuthPageGate = () => {
  const { data: authData, isLoading: authLoading } = useIsAuthenticated();
  const { data: user, isLoading: userLoading } = useGetIdentity<IERPUser>();
  const hasVendorContext = Boolean(getAdminVendorId() && getAdminVendorFacilityId());
  const shouldStayOnLogin = authData?.authenticated && isNexusSuperAdmin(user) && !hasVendorContext;

  if (authLoading || userLoading) {
    return <Outlet />;
  }

  if (!authData?.authenticated || shouldStayOnLogin) {
    return <Outlet />;
  }

  return <NavigateToResource resource="dashboard" />;
};

const MainOutlet = () => {
  const { vendor, vendorFacility } = useVendorGlobalContext();
  const { data: user } = useGetIdentity<IERPUser>();

  return (
    <>
      <Suspense fallback={null}>
        <ForegroundNotifications />
      </Suspense>
      {vendor?.id && (!isNexusSuperAdmin(user) || vendorFacility?.id) && (
        <Suspense fallback={<FoundationLoader />}>
          <Outlet />
        </Suspense>
      )}
      {(!vendor?.id || (isNexusSuperAdmin(user) && !vendorFacility?.id)) && (
        <div
          style={{
            textAlign: 'center',
            padding: 16,
          }}
        >
          <Typography.Title>
            {isNexusSuperAdmin(user)
              ? 'Please Choose a Vendor and Facility'
              : 'Please Choose a Vendor'}
          </Typography.Title>
        </div>
      )}
    </>
  );
};

export default Application;
